import { useCallback, useState, type ReactNode } from 'react';
import type { RunResponse } from '@verifyloop/core';
import { describeError, downloadFile } from '../api/client';
import { cancelRun, getRun } from '../api/endpoints';
import { useResource, useToast } from '../state';
import { formatTimestamp } from '../lib/format';
import { ArtifactRow } from './Artifact';
import { ErrorNotice, LoadingRows } from './Feedback';
import { FailureBundleView } from './FailureBundle';
import { Duration, FailureKindPill, Mono, RunStatusPill, RunTriggerTag } from './Pills';
import { StepResultsView } from './StepViews';

export function RunDetail({
  runId,
  onClose,
}: {
  runId: string;
  onClose: () => void;
}): JSX.Element {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const run = useResource<RunResponse>(() => getRun(runId), [runId], {
    live: ['run.updated', 'run.finished'],
  });

  const data = run.data;
  const execution = data?.run;

  const onCancel = useCallback(async () => {
    if (!execution) return;
    setBusy(true);
    try {
      await cancelRun(execution.id);
      toast.success(`Run ${execution.id} cancelled`, 'reserved credits were refunded.');
      run.reload();
    } catch (error) {
      toast.error(describeError(error));
    } finally {
      setBusy(false);
    }
  }, [execution, toast, run]);

  const onDownload = useCallback(async () => {
    if (!execution) return;
    setBusy(true);
    try {
      await downloadFile(
        `/v1/runs/${execution.id}/artifacts`,
        `verifyloop-${execution.id}.zip`,
      );
      toast.success('Run archive downloaded');
    } catch (error) {
      toast.error(describeError(error));
    } finally {
      setBusy(false);
    }
  }, [execution, toast]);

  return (
    <div className="drawer" role="dialog" aria-modal="false" aria-label={`Run ${runId}`}>
      <header className="drawer__header">
        <div className="drawer__heading">
          {execution ? <RunStatusPill status={execution.status} /> : null}
          {data ? <FailureKindPill kind={execution?.failureKind} /> : null}
          <h2>{data?.test.name ?? `Run ${runId}`}</h2>
          <span className="muted">{data?.project.name}</span>
        </div>
        <div className="drawer__actions">
          {execution && (execution.status === 'running' || execution.status === 'queued') ? (
            <button type="button" className="btn btn--small btn--danger" disabled={busy} onClick={() => void onCancel()}>
              Cancel run
            </button>
          ) : null}
          {execution ? (
            <button type="button" className="btn btn--small" disabled={busy} onClick={() => void onDownload()}>
              Download artifacts (.zip)
            </button>
          ) : null}
          <button type="button" className="btn btn--ghost btn--icon" onClick={onClose} aria-label="Close run detail">
            ×
          </button>
        </div>
      </header>

      <div className="drawer__body">
        {run.loading ? <LoadingRows rows={4} label="Loading run" /> : null}
        {run.error ? <ErrorNotice error={run.error} onRetry={run.reload} title="Could not load this run" /> : null}

        {execution ? (
          <>
            <dl className="run-facts">
              <RunFact label="run id" value={<Mono>{execution.id}</Mono>} />
              <RunFact label="trigger" value={<RunTriggerTag trigger={execution.trigger} />} />
              <RunFact label="started" value={formatTimestamp(execution.startedAt)} />
              <RunFact
                label="finished"
                value={execution.finishedAt ? formatTimestamp(execution.finishedAt) : '-'}
              />
              <RunFact label="duration" value={<Duration ms={execution.durationMs} />} />
              <RunFact label="credits" value={<span className="num">{execution.creditsCharged}</span>} />
              <RunFact label="revision" value={<span className="num">{execution.sequence}</span>} />
              <RunFact label="code version" value={<Mono>{execution.codeVersion}</Mono>} />
              {execution.tunnelClientId ? (
                <RunFact label="tunnel" value={<Mono>{execution.tunnelClientId}</Mono>} />
              ) : null}
              {execution.healed ? <RunFact label="auto-heal" value={<span className="pill pill--info">applied</span>} /> : null}
            </dl>

            {execution.error ? (
              <p className="notice notice--error" role="alert">
                {execution.error}
              </p>
            ) : null}

            {execution.status === 'failed' && data ? (
              <FailureBundleView testId={execution.testId} runId={execution.id} />
            ) : null}

            <section className="panel">
              <div className="panel__header">
                <div className="panel__title">
                  <h3>Steps</h3>
                  <span className="muted">{execution.steps.length} recorded</span>
                </div>
              </div>
              <StepResultsView steps={execution.steps} />
            </section>

            <ArtifactRow runId={execution.id} artifacts={execution.artifacts} title="Run artifacts" />
          </>
        ) : null}
      </div>
    </div>
  );
}

function RunFact({ label, value }: { label: string; value: ReactNode }): JSX.Element {
  return (
    <div className="run-facts__item">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}